import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import YhTable from "../../ui/YhTable";
import YhInput from "../../ui/YhInput";
import YhSelect from "../../ui/YhSelect";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import notifiers from "../../global/constants/NotificationConstants";
import urls from "../../global/constants/UrlConstants";
import CustomLoader from "../../global/components/CustomLoader/CustomLoader";
import { useOrgNameMap } from "../../Shared/Organization/OrganizationSelect";
import {
  buildFormSubmissionColumns,
  invitedByOf,
  submissionAnswerEntries,
} from "../../Shared/FormSubmissions/buildFormSubmissionColumns";
import { selectActiveOrganizationId } from "../../redux/authSlice";
import {
  convertRegistrationToLead,
  exportRegistrationSubmissions,
  getRegistrationForms,
  getRegistrationSubmissions,
} from "./RegistrationService";

const SearchSvg = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const DownloadSvg = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

const RegistrationsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const orgNameMap = useOrgNameMap();
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const formIdFromUrl = useMemo(
    () => new URLSearchParams(location.search).get("formId") || "",
    [location.search]
  );
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [formId, setFormId] = useState(formIdFromUrl);
  const [formOptions, setFormOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [metaData, setMetaData] = useState<any>({});
  const [counts, setCounts] = useState<{
    total: number;
    bySource: Record<string, number>;
    byForm: { formId: string | null; form_name: string; count: number }[];
  }>({ total: 0, bySource: {}, byForm: [] });
  const [detail, setDetail] = useState<any | null>(null);
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setFormId(formIdFromUrl);
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  }, [formIdFromUrl]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getRegistrationForms();
        const list = Array.isArray(res?.data) ? res.data : [];
        setFormOptions(
          list.map((f: any) => ({
            label: f.form_name || "Untitled form",
            value: String(f._id),
          }))
        );
      } catch {
        setFormOptions([]);
      }
    })();
  }, [activeOrganizationId]);

  const applyFormFilter = useCallback(
    (nextId: string) => {
      const next = String(nextId || "");
      setFormId(next);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      const params = new URLSearchParams(location.search);
      if (next) params.set("formId", next);
      else params.delete("formId");
      const qs = params.toString();
      history.replace(`${urls.REGISTRATIONSLIST}${qs ? `?${qs}` : ""}`);
    },
    [history, location.search]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const orgId =
        activeOrganizationId &&
        activeOrganizationId !== "all" &&
        activeOrganizationId !== "*"
          ? String(activeOrganizationId)
          : undefined;
      const res = await getRegistrationSubmissions({
        pageNo: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search,
        formId: formId || undefined,
        organizationId: orgId,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setMetaData(res?.metadata?.[0] || {});
      setCounts({
        total: res?.counts?.total ?? res?.metadata?.[0]?.total ?? 0,
        bySource: res?.counts?.bySource || {},
        byForm: Array.isArray(res?.counts?.byForm) ? res.counts.byForm : [],
      });
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    search,
    formId,
    activeOrganizationId,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const formSelectOptions = useMemo(() => {
    const byId = new Map<string, string>();
    formOptions.forEach((o) => byId.set(String(o.value), o.label));
    counts.byForm.forEach((f) => {
      if (f.formId && !byId.has(String(f.formId))) {
        byId.set(String(f.formId), f.form_name || "Untitled form");
      }
    });
    const countById = new Map(
      counts.byForm
        .filter((f) => f.formId)
        .map((f) => [String(f.formId), f.count])
    );
    return [
      { label: "All forms", value: "all" },
      ...Array.from(byId.entries()).map(([value, label]) => {
        const n = countById.get(value);
        return {
          value,
          label: n != null ? `${label} (${n})` : label,
        };
      }),
    ];
  }, [formOptions, counts.byForm]);

  const openLead = useCallback(
    (leadId: string) => {
      if (!leadId) return;
      history.push(urls.ALLLEADDETAILS.replace(":id", leadId));
    },
    [history]
  );

  const handleConvertToLead = useCallback(
    async (row: any) => {
      const id = row?._id ? String(row._id) : "";
      if (!id || convertingId) return;
      setConvertingId(id);
      try {
        const res = await convertRegistrationToLead(id);
        if (!res?.success) {
          openErrorNotification(
            res?.message || "Unable to convert registration to lead"
          );
          return;
        }
        const leadId = res?.data?.leadId ? String(res.data.leadId) : "";
        openSuccessNotification(res.message || "Converted to lead");
        setRows((prev) =>
          prev.map((r) =>
            String(r._id) === id ? { ...r, leadId: leadId || r.leadId } : r
          )
        );
        setDetail((prev: any) =>
          prev && String(prev._id) === id
            ? { ...prev, leadId: leadId || prev.leadId }
            : prev
        );
      } catch (error: any) {
        openErrorNotification(
          isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
        );
      } finally {
        setConvertingId(null);
      }
    },
    [convertingId]
  );

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    let objectUrl = "";
    try {
      const res = await exportRegistrationSubmissions({
        search,
        formId: formId || undefined,
      });
      if (res?.success && res.fileData && res.filename) {
        const byteCharacters = atob(res.fileData);
        const byteNumbers = new Array(byteCharacters.length)
          .fill(0)
          .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type:
            res.contentType ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = res.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        openSuccessNotification(
          res.count
            ? `Exported ${res.count} registrations`
            : "Registrations exported"
        );
      } else {
        openErrorNotification(
          res?.message || "Could not export registrations. Try again."
        );
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      if (objectUrl) {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
      setExporting(false);
    }
  }, [exporting, search, formId]);

  const columns = useMemo(
    () =>
      buildFormSubmissionColumns({
        rows,
        orgNameMap,
        onViewAnswers: setDetail,
        onOpenLead: openLead,
        onConvertToLead: handleConvertToLead,
        convertingId,
        preferRating: true,
      }),
    [rows, orgNameMap, openLead, handleConvertToLead, convertingId]
  );

  const sourceEntries = Object.entries(counts.bySource || {});

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 shrink-0">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
          Registrations
        </h1>
        <p className="mt-1 text-sm text-[#6B7280]">
          Registration form responses — guest public links or tracked lead
          tokens, including bulk-upload sets.
        </p>
      </div>

      <div className="mb-4 grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-[#6B7280]">Total submitted</p>
          <p className="mt-1 text-2xl font-bold text-[#111827]">
            {counts.total}
          </p>
        </div>
        {sourceEntries.slice(0, 3).map(([key, value]) => (
          <div
            key={key}
            className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-sm"
          >
            <p className="truncate text-xs font-medium capitalize text-[#6B7280]">
              {key || "other"}
            </p>
            <p className="mt-1 text-2xl font-bold text-[#111827]">{value}</p>
          </div>
        ))}
        {!sourceEntries.length ? (
          <>
            <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-[#6B7280]">WhatsApp</p>
              <p className="mt-1 text-2xl font-bold text-[#111827]">0</p>
            </div>
            <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-[#6B7280]">Public</p>
              <p className="mt-1 text-2xl font-bold text-[#111827]">0</p>
            </div>
            <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-[#6B7280]">Forms</p>
              <p className="mt-1 text-2xl font-bold text-[#111827]">
                {counts.byForm.length}
              </p>
            </div>
          </>
        ) : null}
      </div>

      <div className="mb-3 flex shrink-0 flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1 sm:max-w-[420px]">
          <YhInput
            placeholder="Search customer, phone, form source…"
            value={search}
            type="search"
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus={isSearchFocused}
            startAdornment={<SearchSvg />}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            containerClassName="w-full max-w-[420px]"
          />
        </div>
        <div className="w-full min-w-[240px] sm:w-[320px]">
          <YhSelect
            label="Filter by form"
            placeholder="All forms"
            searchable
            searchPlaceholder="Search forms…"
            clearable
            value={formId || "all"}
            onChange={(e: any) => {
              const next = String(e?.target?.value || "");
              applyFormFilter(next === "all" ? "" : next);
            }}
            options={formSelectOptions}
          />
        </div>
        <button
          type="button"
          disabled={exporting || !counts.total}
          onClick={handleExport}
          title="Export all matching registrations, including every form field"
          className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3.5 text-sm font-medium text-[#374151] transition hover:border-[#D4B8C0] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <DownloadSvg />
          {exporting ? "Exporting…" : "Export"}
        </button>
      </div>

      <div className="relative min-h-0 flex-1">
        <YhTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row._id}
          rowHeight={68}
          loading={loading}
          emptyMessage="No registration submissions yet"
          className="h-full min-h-[280px]"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={metaData?.total || 0}
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) =>
            setPaginationModel({ pageSize: s, page: 0 })
          }
        />
        {loading ? <CustomLoader apiResponse={false} /> : null}
      </div>

      {detail ? (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30"
            onClick={() => setDetail(null)}
          />
          <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-white shadow-xl">
            <div className="flex items-start justify-between border-b border-[#EEF0F5] px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                  Form responses
                </p>
                <h2 className="mt-1 text-lg font-bold text-[#111827]">
                  {detail.form_name || "—"}
                </h2>
                <p className="mt-0.5 text-sm text-[#6B7280]">
                  {detail.leadName || "Anonymous"}
                  {detail.organizationName
                    ? ` · ${detail.organizationName}`
                    : ""}
                  {detail.project_name ? ` · ${detail.project_name}` : ""}
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg px-2 py-1 text-sm text-[#6B7280] hover:bg-[#F3F4F6]"
                onClick={() => setDetail(null)}
              >
                Close
              </button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              {detail.leadId ? (
                <button
                  type="button"
                  className="w-full rounded-xl border border-[#DBEAFE] bg-[#EFF6FF] px-3 py-2.5 text-left text-sm font-semibold text-[#1E3A5F] hover:bg-[#DBEAFE]"
                  onClick={() => openLead(String(detail.leadId))}
                >
                  Open lead details →
                </button>
              ) : (
                <button
                  type="button"
                  disabled={
                    !!convertingId &&
                    convertingId === String(detail._id || "")
                  }
                  className="w-full rounded-xl border border-[#F9F0F4] bg-[#FDF5F7] px-3 py-2.5 text-left text-sm font-semibold text-[#A02448] hover:bg-[#F9F0F4] disabled:opacity-60"
                  onClick={() => handleConvertToLead(detail)}
                >
                  {convertingId && convertingId === String(detail._id || "")
                    ? "Converting…"
                    : "Convert to lead →"}
                </button>
              )}
              {detail.teamMemberName ? (
                <div className="rounded-xl border border-[#E8EEF5] bg-[#F4F7FB] px-3 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">
                    Team member
                  </p>
                  <p className="mt-1 text-sm font-semibold capitalize text-[#111827]">
                    {detail.teamMemberName}
                  </p>
                </div>
              ) : null}
              {invitedByOf(detail) ? (
                <div className="rounded-xl border border-[#E8EEF5] bg-[#F4F7FB] px-3 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">
                    Invited by
                  </p>
                  <p className="mt-1 text-sm font-semibold capitalize text-[#111827]">
                    {invitedByOf(detail)}
                  </p>
                </div>
              ) : null}
              {detail.rating != null ? (
                <div className="rounded-xl border border-[#FEF3C7] bg-[#FFFBEB] px-3 py-2 text-sm">
                  <span className="font-semibold text-[#92400E]">Rating: </span>
                  <span className="text-[#D97706]">{detail.rating}★</span>
                </div>
              ) : null}
              {submissionAnswerEntries(detail).length === 0 ? (
                <p className="text-sm text-[#9CA3AF]">No answer fields stored.</p>
              ) : (
                submissionAnswerEntries(detail).map((entry) => (
                  <div
                    key={entry.id}
                    className="rounded-xl border border-[#EEF0F5] bg-[#F8F9FC] px-3 py-2.5"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
                      {entry.label}
                    </p>
                    <p className="mt-1 break-words text-sm text-[#111827]">
                      {entry.value}
                    </p>
                  </div>
                ))
              )}
              <div className="rounded-xl border border-[#EEF0F5] px-3 py-2.5 text-xs text-[#6B7280]">
                <p>
                  Form source:{" "}
                  <span className="font-semibold capitalize text-[#374151]">
                    {detail.formSource || detail.source || "—"}
                  </span>
                </p>
                {detail.source &&
                String(detail.source).toLowerCase() !==
                  String(detail.formSource || "").toLowerCase() ? (
                  <p className="mt-1">
                    Opened from:{" "}
                    <span className="font-semibold capitalize text-[#374151]">
                      {detail.source === "meta"
                        ? "Meta ads"
                        : detail.source === "link"
                          ? "Manual link"
                          : detail.source}
                    </span>
                  </p>
                ) : null}
                <p className="mt-1">
                  Lead source:{" "}
                  <span className="font-semibold capitalize text-[#374151]">
                    {detail.leadSource || "—"}
                  </span>
                </p>
                <p className="mt-1">
                  Bulk upload:{" "}
                  <span className="font-semibold text-[#374151]">
                    {detail.bulkJobName || "—"}
                  </span>
                </p>
                {detail.leadStage ? (
                  <p className="mt-1">
                    Stage:{" "}
                    <span className="font-semibold capitalize text-[#374151]">
                      {detail.leadStage}
                    </span>
                  </p>
                ) : null}
                {detail.utmSource || detail.utmMedium || detail.utmCampaign ? (
                  <p className="mt-1">
                    UTM: {detail.utmSource || "—"}
                    {detail.utmMedium ? ` / ${detail.utmMedium}` : ""}
                    {detail.utmCampaign ? ` / ${detail.utmCampaign}` : ""}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default RegistrationsPage;
